'use client'
/**
 * The contact form: one screen (V3Ask), the same send as the old five-step
 * sheet. Owns nothing the primitive does not need: the inquiry options, the
 * tour-time options, the SMS consent line, and the send itself — FormData to
 * submitContactForm, then the Meta and GA lead events the sheet fired.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
// Direct import — the v3 barrel also re-exports server-only place modules
// (V3PlaceDocuments → lib/data → next/headers), which breaks this client module
// under webpack. V3Ask is itself a client primitive.
import { V3Ask, type V3AskField, type V3AskResult } from '@/components/site/v3/V3Ask.client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SmsConsentDisclosure } from '@/components/site/SmsConsentDisclosure'
import { FormAdConsentNotice, FormAdCookieBox, useFormConsentRestricted } from '@/components/site/FormAdConsent'
import { writeFormAdConsent } from '@/lib/identity/form-ad-consent'
import './contact-ask.css'
import { getLpContext, readRrSessionId, trackTourRequested } from '@/lib/tracking'
import { appendPostedGaIds, primeGaBrowserIds } from '@/lib/analytics/ga-browser-ids'
import { submitContactForm } from '../actions'
import { publishTourConfirmation } from '@/lib/contact/publish-tour-confirmation'
import { CONTACT_FIELD_IDS, CONTACT_TRAP } from './contact-constants'
import { ContactField } from './ContactField.client'
import { ContactFieldDemoContext, type ContactFieldDemo } from './contact-field-demo'
import { useShotTriggers } from './use-shot-triggers'

/** getLpContext keys the contact action reads (postedLeadAttribution). */
const LP_POST_KEYS = ['lp_source', 'lp_medium', 'lp_campaign', 'lp_content', 'lp_term', 'gclid', 'fbclid'] as const

const BASE_INQUIRY_OPTIONS = [
  { value: 'Buying', label: 'Buying' },
  { value: 'Selling', label: 'Selling' },
  { value: 'Both', label: 'Both' },
  { value: 'General Inquiry', label: 'General inquiry' },
  { value: 'Relocation', label: 'Relocation' },
  { value: 'Join the team', label: 'Join the team' },
] as const

const TOUR_TIME_OPTIONS = [
  'As soon as possible',
  'This weekend',
  'A weekday evening',
  'A weekday daytime',
  'Flexible, broker suggests times',
] as const

const SENT_PREVIEW = {
  ok: true as const,
  heading: 'Message received',
  body: 'A broker gets this now and answers you personally. A confirmation is on its way to your inbox.',
  detail: 'Sent: General inquiry',
  door: { href: '/book', label: 'Or pick a time now' },
  again: 'Send another message',
}

export function ContactAsk({
  defaultInquiryType,
  listingKey,
  intent,
  listingSummary,
}: {
  defaultInquiryType?: string
  listingKey?: string
  intent?: 'tour' | 'question'
  listingSummary?: string
}) {
  const isTour = intent === 'tour'
  const [smsConsent, setSmsConsent] = useState(false)
  const [adCookies, setAdCookies] = useState(false)
  const [adMatch, setAdMatch] = useState(false)
  const restricted = useFormConsentRestricted()
  const [fieldDemo, setFieldDemo] = useState<ContactFieldDemo>('idle')
  const [previewSent, setPreviewSent] = useState(false)

  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('taste_state')
    if (raw === 'error' || raw === 'success') setFieldDemo(raw)
    if (raw === 'sent') setPreviewSent(true)
  }, [])

  // Ask gtag for the GA ids on load, so the submit can post them when the
  // `_ga` cookies are not readable yet.
  useEffect(() => {
    primeGaBrowserIds()
  }, [])

  const inquiryOptions = useMemo(() => {
    const extra =
      defaultInquiryType && !BASE_INQUIRY_OPTIONS.some((o) => o.value === defaultInquiryType)
        ? [{ value: defaultInquiryType, label: defaultInquiryType }]
        : []
    return [...BASE_INQUIRY_OPTIONS, ...extra]
  }, [defaultInquiryType])

  const fields = useMemo<V3AskField[]>(
    () => [
      {
        name: 'inquiryType',
        label: 'How can we help',
        kind: 'select',
        required: true,
        options: inquiryOptions,
        defaultValue: defaultInquiryType ?? 'General Inquiry',
        span: 'full',
      },
      ...(isTour
        ? [
            {
              name: 'tourTime',
              label: 'When works',
              kind: 'select' as const,
              required: true,
              span: 'full' as const,
              options: TOUR_TIME_OPTIONS.map((t) => ({ value: t, label: t })),
            },
          ]
        : []),
      { id: CONTACT_FIELD_IDS.name, name: 'name', label: 'Name', required: true, autoComplete: 'name' },
      { id: CONTACT_FIELD_IDS.email, name: 'email', label: 'Email', kind: 'email', required: true, autoComplete: 'email' },
      {
        id: CONTACT_FIELD_IDS.phone,
        name: 'phone',
        label: 'Phone',
        kind: 'tel',
        autoComplete: 'tel',
        hint: 'for a text back',
        span: 'full',
      },
      {
        id: CONTACT_FIELD_IDS.message,
        name: 'message',
        label: isTour ? 'Anything the broker should know' : 'Your message',
        kind: 'textarea',
        required: !isTour,
        maxLength: 2000,
        placeholder: listingSummary ? `About ${listingSummary}` : undefined,
      },
    ],
    [inquiryOptions, defaultInquiryType, isTour, listingSummary],
  )

  const send = useCallback(
    async (answers: Readonly<Record<string, string>>): Promise<V3AskResult> => {
      const formData = new FormData()
      formData.set('name', answers.name ?? '')
      formData.set('email', answers.email ?? '')
      formData.set('phone', answers.phone ?? '')
      formData.set('inquiryType', answers.inquiryType || defaultInquiryType || 'General Inquiry')
      let message = answers.message ?? ''
      if (isTour && answers.tourTime) {
        message = `Tour request. Preferred time: ${answers.tourTime}.${message ? `\n${message}` : ''}`
      }
      formData.set('message', message)
      if (smsConsent) formData.set('smsConsent', 'yes')
      // Memo 5.4: a checked ad-cookie box writes the banner grant. An empty
      // box writes nothing. The EU/UK match box is a separate consent.
      writeFormAdConsent(adCookies) // hydration-safe: submit handler, not render
      if (restricted && adMatch) formData.set('adMatchConsent', 'yes')
      // Forward the trap verbatim: hardcoding it empty is the same as no trap.
      formData.set(CONTACT_TRAP.name, answers[CONTACT_TRAP.name] ?? '')
      const rrSession = readRrSessionId() // hydration-safe: event/effect storage only
      if (rrSession) formData.set('sessionId', rrSession)
      if (listingKey) formData.set('listingKey', listingKey)
      // The server writes the confirmation from this: "your tour request" reads
      // wrong on a plain question, and the reverse reads worse.
      if (intent) formData.set('intent', intent)
      appendPostedGaIds(formData)
      // The landing link's utm_* and ad click ids, so the lead keeps its gclid /
      // fbclid when the Referer is stripped.
      const lp = getLpContext('contact') // hydration-safe: submit handler, not render
      for (const key of LP_POST_KEYS) {
        const value = lp[key]
        if (value) formData.set(key, value)
      }
      if (!answers.name?.trim() || !answers.email?.trim() || (!isTour && !answers.message?.trim())) {
        return { ok: false, message: 'Fill the required fields. The boxes that need a value will shake.' }
      }
      const result = await submitContactForm(formData)
      if (!result.success) {
        return { ok: false, message: result.error || 'The message did not send. Call or text instead, or try again.' }
      }
      // No eventId = the intake screen flagged the submit (FUNNEL-1): the visitor
      // still sees the sent state, the ad platforms and GA4 do not count a lead.
      if (result.eventId) {
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead', { content_name: formData.get('inquiryType') }, { eventID: result.eventId })
        }
        // GA4 generate_lead is sent once, by the server action (lib/lead-tracking.ts).
        if (isTour) {
          trackTourRequested({
            listing_key: listingKey,
            form: 'contact',
            form_id: 'contact',
            lead_type: 'buyer_showing',
          })
        }
      }
      // SITE-09: the sent state says what is happening now. No duration promise.
      return {
        ok: true,
        heading: isTour ? 'Tour request received' : 'Message received',
        body: isTour
          ? `${publishTourConfirmation(listingSummary)} A confirmation is on its way to your inbox.`
          : 'A broker gets this now and answers you personally. A confirmation is on its way to your inbox.',
        detail: `Sent: ${String(formData.get('inquiryType') ?? '')}${listingSummary ? ` · ${listingSummary}` : ''}`,
        door: { href: '/book', label: 'Or pick a time now' },
        again: 'Send another message',
      }
    },
    [adCookies, adMatch, defaultInquiryType, intent, isTour, listingKey, listingSummary, restricted, smsConsent],
  )

  const shotTriggers = useShotTriggers()

  return (
    <ContactFieldDemoContext.Provider value={fieldDemo}>
      <div className="contact-ask-root">
        {shotTriggers ? (
        <div className="contact-ask__taste" aria-hidden="true">
          <Button type="button" variant="ghost" tabIndex={-1} data-taste="error-open" onClick={() => setFieldDemo('error')}>
            Show field error
          </Button>
          <Button type="button" variant="ghost" tabIndex={-1} data-taste="success-open" onClick={() => setFieldDemo('success')}>
            Show field success
          </Button>
          <Button
            type="button"
            variant="ghost"
            tabIndex={-1}
            data-taste="sent-open"
            onClick={() => {
              setFieldDemo('idle')
              setPreviewSent(true)
            }}
          >
            Show sent
          </Button>
        </div>
        ) : null}
        <V3Ask
          id="write"
          className="contact-ask"
          eyebrow="Talk to a broker"
          heading={isTour ? 'Request a tour' : 'Send a message'}
          headingLevel={2}
          lede={listingSummary ? (isTour ? listingSummary : `About ${listingSummary}`) : undefined}
          fields={fields}
          Field={ContactField}
          consent={
            <>
              {/* Honeypot (FUNNEL-1): inside the form so V3Ask collects it, off
                  screen by CSS (a scripted filler skips display:none), out of
                  the tab order, hidden from assistive tech. */}
              <div className="contact-ask__trap" aria-hidden="true">
                <Label htmlFor="contact-trap">{CONTACT_TRAP.label}</Label>
                <Input id="contact-trap" name={CONTACT_TRAP.name} type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
              </div>
              <FormAdCookieBox checked={adCookies} onCheckedChange={setAdCookies} />
              <SmsConsentDisclosure checked={smsConsent} onCheckedChange={setSmsConsent} />
            </>
          }
          notice={
            <FormAdConsentNotice
              surface="ask"
              restricted={restricted}
              adMatchChecked={adMatch}
              onAdMatchCheckedChange={setAdMatch}
            />
          }
          submitLabel={isTour ? 'Request a tour' : 'Send message'}
          onSubmit={send}
          previewSent={previewSent}
          previewSentResult={SENT_PREVIEW}
        />
      </div>
    </ContactFieldDemoContext.Provider>
  )
}
